import { useState } from "react";
import { Tag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Bulk-price manager for a single event — sets the same SEK price on every
 * photo the caller owns in that event in one operation. Used on the event
 * manage page so photographers can re-price 1000+ photos with a single click.
 *
 * Props:
 *   - eventId: required
 *   - photoCount: shown in confirmation copy
 *   - onChanged: optional refresh callback
 */
export default function BulkPriceManager({ eventId, photoCount = 0, onChanged }) {
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const p = Number(price);
    if (!p || p < 10 || p > 999) {
      toast.error("Ange ett pris mellan 10 och 999 kr");
      return;
    }
    if (!window.confirm(
      `Sätta priset ${p} kr på alla ${photoCount || "(?)"} foton i detta event? Kan inte ångras.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/events/${eventId}/photos/bulk-price`, { price_sek: p });
      toast.success(`✓ ${r.data.updated} foton uppdaterade till ${r.data.price_sek} kr`);
      setPrice("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Misslyckades");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-5" data-testid="bulk-price-manager">
      <p className="label-overline">Prissättning</p>
      <h3 className="font-display font-bold uppercase text-white text-lg mb-1">
        Bulk-pris för hela eventet
      </h3>
      <p className="text-white/50 text-xs mb-3">
        Sätt samma pris på alla {photoCount || "dina"} foton i detta event på en gång. Påverkar inte andra fotografers bilder.
      </p>
      <div className="flex flex-wrap items-stretch gap-2 max-w-md">
        <div className="relative flex-1 min-w-[180px]">
          <input
            type="number"
            min={10}
            max={999}
            step={1}
            inputMode="numeric"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="t.ex. 149"
            className="input-field pr-10 w-full"
            data-testid="bulk-price-input"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-sm pointer-events-none">
            kr
          </span>
        </div>
        <button
          onClick={submit}
          disabled={busy || !price}
          className="btn-primary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="bulk-price-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
          Uppdatera alla
        </button>
      </div>
    </div>
  );
}
